import {
  SegmentRoot,
  SegmentImageCard,
  SegmentImage,
  SegmentInputSection,
} from "~/features/configurator/Shaft/substeps/ShaftMachiningSubstep/components/SegmentCommonStyles";
import { SimpleCsticNumberField } from "~/features/configurator/Shaft/components/SimpleCsticNumberField";
import { SimpleCsticSelectField } from "~/features/configurator/Shaft/components/SimpleCsticSelectField";
import { SimpleCsticId } from "~/features/configurator/Linear/models";
import { selectCstic, selectCsticExpanded } from "~/features/configurator/Linear/redux/selectCstic";
import { useSelector } from "react-redux";
import { SubstepType } from "~/features/configurator/Linear/dtos";
import { chamfer } from "~/features/configurator/Shaft/inch/inchCsticConstants";
import { useXmlConfiguratorTranslations } from "~/features/configurator/common/useXmlConfiguratorTranslations";
import { SimpleCsticTextField } from "~/features/configurator/Shaft/components/SimpleCsticTextField";
import LanguageAwareExternalContent from "~/features/configurator/Linear/components/LanguageAwareExternalContent";
import {
  isChamferManualWidth,
  validateChamferSegment,
  type ValidationMode,
} from "~/features/configurator/Shaft/shaftConfiguratorHelper";
import type { RootState } from "~/app/store";
import {
  ErrorMessage,
  selectIsMachiningSubstepInvalid,
} from "~/features/configurator/Shaft/metric/components/MetricShaftMachiningExtraSubstep/MetricShaftMachiningExtraSubstepHelper";
import { selectMainKmat } from "~/features/configurator/Shaft/common/commonShaftHelpers";
import { selectConflictedCstics } from "~/features/configurator/Linear/redux/selectors";

import { KmatType } from "~/features/configurator/Linear/kmatType";

interface Props {
  className?: string;
  side: "left" | "right";
}

const CONFIG = {
  left: {
    chamferAngle: {
      csticId: SimpleCsticId(chamfer.left.angle),
      label: "configurator.shaft.step.2.substep.3.extraFields.chamferAngle.title",
    },
    chamferWidthSF: {
      csticId: SimpleCsticId(chamfer.left.width.sf),
      label: "configurator.shaft.step.2.substep.3.extraFields.chamferWidth.title",
    },
    chamferWidthNF: {
      csticId: SimpleCsticId(chamfer.left.width.nf),
      label: "configurator.shaft.step.2.substep.3.extraFields.chamferWidth.title",
    },
    minmax: SimpleCsticId(chamfer.left.width.minmax),
    image: "configurator.shaft.step.2.substep.3.extraFields.left.image",
  },
  right: {
    chamferAngle: {
      csticId: SimpleCsticId(chamfer.right.angle),
      label: "configurator.shaft.step.2.substep.3.extraFields.chamferAngle.title",
    },
    chamferWidthSF: {
      csticId: SimpleCsticId(chamfer.right.width.sf),
      label: "configurator.shaft.step.2.substep.3.extraFields.chamferWidth.title",
    },
    chamferWidthNF: {
      csticId: SimpleCsticId(chamfer.right.width.nf),
      label: "configurator.shaft.step.2.substep.3.extraFields.chamferWidth.title",
    },
    minmax: SimpleCsticId(chamfer.right.width.minmax),
    image: "configurator.shaft.step.2.substep.3.extraFields.right.image",
  },
};

export const InchShaftChamferSegment = (props: Props) => {
  const t = useXmlConfiguratorTranslations().t!;

  function translate<T extends { label: string }>(data: T) {
    return { ...data, label: t(data.label) };
  }

  const config = CONFIG[props.side];
  const angleCstic = useSelector(selectCstic(config.chamferAngle.csticId));
  const minmaxCstic = useSelector(selectCstic(config.minmax));

  const helperLabel = minmaxCstic?.value;

  const isWidthEditable = isChamferManualWidth(angleCstic?.value);

  const errors = useSelector(
    selectErrors({
      angle: config.chamferAngle.csticId.key,
      width: {
        sf: config.chamferWidthSF.csticId.key,
        nf: config.chamferWidthNF.csticId.key,
      },
    }),
  );
  const invalidWidth = !!errors.find((error) => error.cstic === config.chamferWidthSF.csticId.key);
  const invalidAngle = !invalidWidth && !!errors.find((error) => error.cstic === config.chamferAngle.csticId.key);

  return (
    <SegmentRoot className={props.className}>
      <SegmentImageCard>
        <SegmentImage src={t(config.image)} alt="" />
      </SegmentImageCard>
      <SegmentInputSection>
        {invalidAngle && <ErrorMessage />}
        <SimpleCsticSelectField
          {...translate(config.chamferAngle)}
          substepType={SubstepType.ShaftMachining}
          shrink={true}
          renderValue={(_, index) => {
            return (
              // <Stack direction={"row"} gap={1}>
              <LanguageAwareExternalContent content={DROPDOWN_LABELS[index]} />
              // <LinearTileV2.StandardIndicator />
              // </Stack>
            );
          }}
        />
        {isWidthEditable && (
          <>
            {invalidWidth && <ErrorMessage variant={"input"} />}
            <SimpleCsticNumberField
              {...translate(config.chamferWidthSF)}
              substepType={SubstepType.ShaftMachining}
              decimalScale={4}
              helperLabel={helperLabel}
              required
            />
          </>
        )}
        {!isWidthEditable && (
          <SimpleCsticTextField
            {...translate(config.chamferWidthNF)}
            substepType={SubstepType.ShaftMachining}
            disabled={true}
          />
        )}
      </SegmentInputSection>
    </SegmentRoot>
  );
};

const DROPDOWN_LABELS = [
  "configurator.shaft.step.2.substep.3.extraFields.chamferAngle.option1",
  "configurator.shaft.step.2.substep.3.extraFields.chamferAngle.option2",
];

// Get the 2nd param of isAxialThreadFullyConfigured as a type
type Config = Parameters<typeof validateChamferSegment>[0];
const selectErrors = (config: Config) => (state: RootState) => {
  const isSubstepInvalid = selectIsMachiningSubstepInvalid(state);
  const { isDirty } = selectCsticExpanded(
    SimpleCsticId(config.width.sf, KmatType.Main),
    SubstepType.ShaftMachining,
  )(state);

  const mode: ValidationMode = isDirty || isSubstepInvalid ? "full" : "feedback";
  const kmat = selectMainKmat(state);
  const conflictedCstics = selectConflictedCstics(state);
  return validateChamferSegment(config, { kmat, conflictedCstics, mode });
};
